import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '../services/supabase';
import { Transaction } from '@flows.cash/types';
import { storage } from '../utils/storage';
import { Platform } from '../utils/platform';

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

export type RuleField = 'description' | 'category' | 'payee' | 'notes' | 'tags';
export type RuleOperator = 'contains' | 'equals' | 'startsWith' | 'endsWith';

export interface RuleAction {
  field: RuleField;
  value: string;
}

export interface Rule {
  id: string;
  user_id: string;
  name: string;
  active: boolean;
  condition: {
    field: RuleField;
    operator: RuleOperator;
    value: string;
  };
  actions: RuleAction[];
  created_at: string;
  updated_at: string;
}

interface RulesState {
  rules: Rule[];
  isLoading: boolean;
  error: string | null;
}

export interface RulesStore extends RulesState {
  fetchRules: (userId: string) => Promise<Rule[]>;
  addRule: (rule: Omit<Rule, 'id' | 'created_at' | 'updated_at'>) => Promise<Rule>;
  updateRule: (ruleId: string, updates: Partial<Rule>) => Promise<void>;
  deleteRule: (ruleId: string) => Promise<void>;
  applyRules: (transaction: Partial<Transaction> & { description: string }) => Partial<Transaction>;
}

export const useRulesStore = create<RulesStore>()(
  persist(
    (set, get) => ({
      rules: [],
      isLoading: false,
      error: null,

      fetchRules: async (userId: string) => {
        try {
          set({ isLoading: true, error: null });

          const { data, error } = await supabase
            .from('rules')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

          if (error) throw error;

          set({ rules: data || [], isLoading: false });
          return data || [];
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch rules';
          set({ error: errorMessage, isLoading: false });
          console.error('Error fetching rules:', error);
          return [];
        }
      },

      addRule: async (rule) => {
        try {
          set({ isLoading: true, error: null });

          const newRule = {
            ...rule,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          const { data, error } = await supabase
            .from('rules')
            .insert([newRule])
            .select()
            .single();

          if (error) throw error;

          set(state => ({
            rules: [...state.rules, data],
            isLoading: false
          }));

          return data;
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to add rule';
          set({ error: errorMessage, isLoading: false });
          console.error('Error adding rule:', error);
          throw error;
        }
      },

      updateRule: async (ruleId, updates) => {
        try {
          set({ isLoading: true, error: null });

          const { error } = await supabase
            .from('rules')
            .update({
              ...updates,
              updated_at: new Date().toISOString()
            })
            .eq('id', ruleId);

          if (error) throw error;

          set(state => ({
            rules: state.rules.map(rule => 
              rule.id === ruleId ? { ...rule, ...updates, updated_at: new Date().toISOString() } : rule
            ),
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to update rule';
          set({ error: errorMessage, isLoading: false });
          console.error('Error updating rule:', error);
          throw error;
        }
      },

      deleteRule: async (ruleId) => {
        try {
          set({ isLoading: true, error: null });

          const { error } = await supabase
            .from('rules')
            .delete()
            .eq('id', ruleId);

          if (error) throw error;

          set(state => ({
            rules: state.rules.filter(rule => rule.id !== ruleId),
            isLoading: false
          }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete rule';
          set({ error: errorMessage, isLoading: false });
          console.error('Error deleting rule:', error);
          throw error;
        }
      },

      applyRules: (transaction) => {
        const { rules } = get();
        
        if (!rules.length) {
          return transaction;
        }

        let result = { ...transaction };
        
        // Only apply active rules
        const activeRules = rules.filter(rule => rule.active);
        
        for (const rule of activeRules) {
          const { condition, actions } = rule;
          
          // Check if the condition matches
          let matches = false;
          const fieldValue = transaction[condition.field as keyof typeof transaction] as string;
          
          if (typeof fieldValue === 'string') {
            switch (condition.operator) {
              case 'contains':
                matches = fieldValue.toLowerCase().includes(condition.value.toLowerCase());
                break;
              case 'equals':
                matches = fieldValue.toLowerCase() === condition.value.toLowerCase();
                break;
              case 'startsWith':
                matches = fieldValue.toLowerCase().startsWith(condition.value.toLowerCase());
                break;
              case 'endsWith':
                matches = fieldValue.toLowerCase().endsWith(condition.value.toLowerCase());
                break;
            }
          }
          
          // If the condition matches, apply the actions
          if (matches) {
            for (const action of actions) {
              // Apply the action to the transaction
              result = {
                ...result,
                [action.field]: action.value
              };
            }
          }
        }
        
        return result;
      }
    }),
    {
      name: 'flows-rules-store',
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => ({ rules: state.rules }),
    }
  )
); 